import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import UserStatsOverview from '../components/Dashboard/UserStatsOverview';
import ManagedMemorialsList from '../components/Dashboard/ManagedMemorialsList';
import RecentTributesHistory from '../components/Dashboard/RecentTributesHistory';
import MilestoneWidget from '../components/Memorial/MilestoneWidget';
import CartDrawer from '../components/Marketplace/CartDrawer';
import DocuVaultDocumentCard from '../components/DocuVault/DocuVaultDocumentCard';
import DocuVaultUploadModal from '../components/DocuVault/DocuVaultUploadModal';
import DocuVaultAuthModal from '../components/DocuVault/DocuVaultAuthModal';
import DocuVaultStorageMeter from '../components/DocuVault/DocuVaultStorageMeter';
import DocuVaultUpgradePlans from '../components/DocuVault/DocuVaultUpgradePlans';
import DocuVaultExpiryReminders from '../components/DocuVault/DocuVaultExpiryReminders';
import DocuVaultAccessLogModal from '../components/DocuVault/DocuVaultAccessLogModal';
import DocuVaultEmergencyReleaseModal from '../components/DocuVault/DocuVaultEmergencyReleaseModal';
import DocuVaultEmergencyReviewPanel from '../components/DocuVault/DocuVaultEmergencyReviewPanel';
import MyEventsTab from '../components/Dashboard/MyEventsTab';
import FamilyNetworkTab from '../components/Dashboard/FamilyNetworkTab';
import { mockMemorials } from '../data/mockData';
import { useEcommerce } from '../context/EcommerceContext';
import { useAuth } from '../context/AuthContext';
import {
  fetchDocuments,
  deleteDocument,
  logAccess,
  DOCUMENT_CATEGORIES,
} from '../services/docuvaultService';
import {
  fetchSubscription,
  computeUsedMb,
  getTierById,
  loadStorageTiers,
  STORAGE_TIERS,
} from '../services/subscriptionService';

const {
  FiUser, FiLogOut, FiActivity, FiHeart, FiShoppingBag,
  FiCheck, FiShield, FiInfo, FiX, FiCrown,
  FiUploadCloud, FiFolder, FiRefreshCw, FiAlertTriangle,
  FiSearch, FiRadio, FiUsers, FiGitBranch, FiZap, FiList,
} = FiIcons;

const ROLE_INFO = [
  { label: 'Memorial Creator',    desc: 'Full authority over your own memorials — editing, moderating tributes, and managing access.' },
  { label: 'Site Administrator',  desc: 'Platform-wide oversight — merchants, moderation, platform settings, and analytics.' },
  { label: 'Super Administrator', desc: 'Highest privilege — manages all Site Administrators, their permissions, and access levels.' },
];

// ── DocuVaultTab ──────────────────────────────────────────────────────────────
function DocuVaultTab() {
  const [docs,           setDocs]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [fetchError,     setFetchError]     = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery,    setSearchQuery]    = useState('');
  const [uploadOpen,     setUploadOpen]     = useState(false);
  const [authDoc,        setAuthDoc]        = useState(null);
  const [deleteError,    setDeleteError]    = useState('');
  const [plansOpen,      setPlansOpen]      = useState(false);
  const [accessLogOpen,  setAccessLogOpen]  = useState(false);
  const [accessLogDoc,   setAccessLogDoc]   = useState(null);
  const [emergencyDoc,   setEmergencyDoc]   = useState(null);

  // ── Subscription + live tier state ────────────────────────────────────────
  const [subscription,   setSubscription]   = useState(null);
  const [subLoading,     setSubLoading]     = useState(true);
  const [tiers,          setTiers]          = useState(STORAGE_TIERS);  // initialise from cache
  const [tiersFromApi,   setTiersFromApi]   = useState(false);          // true once live data loaded

  // ── Load subscription + live tiers from Plans API ─────────────────────────
  const loadSubscription = useCallback(async () => {
    setSubLoading(true);
    try {
      // Always fetch fresh tier data from Plans API
      const freshTiers = await loadStorageTiers();
      setTiers(freshTiers);
      setTiersFromApi(true);
    } catch {
      // loadStorageTiers never throws — it falls back internally
      setTiers(STORAGE_TIERS);
    }
    const sub = await fetchSubscription();
    setSubscription(sub);
    setSubLoading(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setFetchError('');
    try {
      const data = await fetchDocuments({ category: categoryFilter });
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      setFetchError(e.message || 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => { load(); loadSubscription(); }, [load, loadSubscription]);

  // ── Derive storage info from live tiers ───────────────────────────────────
  const activeTier    = subscription
    ? tiers.find(t => t.plan_key === subscription.plan_id || t.id === subscription.plan_id) || tiers[0]
    : tiers[0];
  const usedMb        = computeUsedMb(docs);
  const limitMb       = activeTier.storage_mb;
  const isStorageFull = usedMb >= limitMb;

  const handleDelete = async (id, s3Key) => {
    setDeleteError('');
    try {
      const doc = docs.find(d => d.id === id);
      await deleteDocument(id, s3Key);
      logAccess(id, 'delete', { docName: doc?.original_name || '', s3Key });
      setDocs(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setDeleteError(e.message || 'Deletion failed. Please try again.');
      throw e;
    }
  };

  const handleView = (doc) => {
    logAccess(doc.id, 'view', { docName: doc.original_name, s3Key: doc.s3_key });
    if (doc.s3_url) window.open(doc.s3_url, '_blank', 'noopener,noreferrer');
  };

  const handleUploadClick = () => {
    if (isStorageFull) { setPlansOpen(true); return; }
    setUploadOpen(true);
  };

  const handleExpiryRenewed = useCallback((docId, newDate) => {
    logAccess(docId, 'renew', { newExpiryDate: newDate });
    setDocs(prev => prev.map(d => d.id === docId ? { ...d, expiry_date: newDate } : d));
  }, []);

  const filtered = docs.filter(d => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      d.original_name?.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q) ||
      d.file_category?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-white rounded-[40px] p-8 border border-brand-dark shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-accent-navy to-[#1a2a5e] rounded-[20px] flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiShield} className="w-7 h-7 text-accent-gold" />
            </div>
            <div>
              <h2 className="text-2xl font-serif text-accent-navy">DocuVault</h2>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                AES-256 Encrypted · AWS S3 · Multi-Party Auth
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPlansOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 border-2 border-accent-gold text-accent-gold rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold hover:text-white transition-all"
            >
              <SafeIcon icon={FiZap} className="w-3.5 h-3.5" />
              {subLoading ? 'Loading…' : `${activeTier.name} Plan`}
            </button>
            <button onClick={() => { load(); loadSubscription(); }}
              className="p-2.5 border-2 border-brand-dark text-text-muted hover:text-accent-navy hover:border-accent-navy rounded-xl transition-all" title="Refresh">
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${(loading || subLoading) ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => { setAccessLogDoc(null); setAccessLogOpen(true); }}
              className="p-2.5 border-2 border-brand-dark text-text-muted hover:text-accent-navy hover:border-accent-navy rounded-xl transition-all"
              title="Access Log"
            >
              <SafeIcon icon={FiList} className="w-4 h-4" />
            </button>
            <button
              onClick={handleUploadClick}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md ${
                isStorageFull
                  ? 'bg-red-500 text-white hover:bg-red-600 shadow-red-200'
                  : 'bg-accent-gold text-white hover:bg-accent-gold/90 shadow-accent-gold/20'
              }`}
            >
              <SafeIcon icon={isStorageFull ? FiZap : FiUploadCloud} className="w-4 h-4" />
              {isStorageFull ? 'Upgrade to Upload' : 'Upload Document'}
            </button>
          </div>
        </div>

        {/* Storage Meter — passes isLiveData so meter shows "Live" badge */}
        {!subLoading && (
          <div className="mb-5">
            <DocuVaultStorageMeter
              usedMb={usedMb}
              limitMb={limitMb}
              planName={activeTier.name}
              onUpgrade={() => setPlansOpen(true)}
              isLiveData={tiersFromApi}
            />
          </div>
        )}

        {/* Search & Category filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search documents…"
              className="w-full pl-11 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {DOCUMENT_CATEGORIES.slice(0, 5).map(cat => (
              <button key={cat.id} onClick={() => setCategoryFilter(cat.id)}
                className={`flex-shrink-0 px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                  categoryFilter === cat.id ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy/40'
                }`}>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Emergency Release Review Panel */}
      <DocuVaultEmergencyReviewPanel onRefresh={load} />

      {/* Expiry Reminders Panel */}
      {!loading && (
        <DocuVaultExpiryReminders
          docs={docs}
          onView={handleView}
          onRenewed={handleExpiryRenewed}
        />
      )}

      {/* Error banners */}
      {deleteError && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700">
          <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1"><p className="font-bold mb-0.5">Deletion Error</p><p className="font-light text-[11px]">{deleteError}</p></div>
          <button onClick={() => setDeleteError('')} className="text-red-400 hover:text-red-600"><SafeIcon icon={FiX} className="w-4 h-4" /></button>
        </motion.div>
      )}
      {fetchError && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-800">
          <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{fetchError}</span>
          <button onClick={load} className="text-[10px] font-black uppercase tracking-widest text-amber-700 hover:text-amber-900 flex items-center gap-1">
            <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
      )}

      {/* Document grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-44 bg-white rounded-[28px] border-2 border-brand-dark animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-[40px] p-16 border border-brand-dark text-center">
          <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiFolder} className="w-10 h-10 text-accent-gold/40" />
          </div>
          <h3 className="text-2xl font-serif text-accent-navy mb-2">
            {searchQuery ? 'No documents match your search' : 'No Documents Yet'}
          </h3>
          <p className="text-text-muted font-light mb-8 max-w-sm mx-auto">
            {searchQuery ? 'Try a different search term or clear the filter.' : 'Upload your first document to DocuVault — encrypted, secure, and always accessible.'}
          </p>
          {!searchQuery && !isStorageFull && (
            <button onClick={() => setUploadOpen(true)}
              className="btn-primary py-4 px-10 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 mx-auto">
              <SafeIcon icon={FiUploadCloud} className="w-4 h-4" /> Upload First Document
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((doc, i) => (
            <DocuVaultDocumentCard key={doc.id} doc={doc} index={i}
              onDelete={handleDelete}
              onAuthorize={(d) => setAuthDoc(d)}
              onView={handleView}
              onAccessLog={(d) => { setAccessLogDoc(d); setAccessLogOpen(true); }}
              onEmergencyRelease={(d) => setEmergencyDoc(d)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <DocuVaultUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)}
        onSuccess={() => { setUploadOpen(false); load(); }} />
      {authDoc && (
        <DocuVaultAuthModal isOpen={!!authDoc} document={authDoc}
          onClose={() => setAuthDoc(null)}
          onSuccess={() => {
            logAccess(authDoc.id, 'authorize', { docName: authDoc.original_name });
            setAuthDoc(null);
            load();
          }} />
      )}
      <DocuVaultAccessLogModal
        isOpen={accessLogOpen}
        onClose={() => setAccessLogOpen(false)}
        document={accessLogDoc}
      />
      <DocuVaultEmergencyReleaseModal
        isOpen={!!emergencyDoc}
        document={emergencyDoc}
        onClose={() => setEmergencyDoc(null)}
        onSuccess={() => { setEmergencyDoc(null); load(); }}
      />
      {/* DocuVaultUpgradePlans fetches its own fresh tiers on open */}
      <DocuVaultUpgradePlans
        isOpen={plansOpen}
        onClose={() => setPlansOpen(false)}
        currentPlanId={activeTier.plan_key || activeTier.id}
        onPlanChanged={(tier) => {
          setSubscription({ plan_id: tier.plan_key, plan_name: tier.name, storage_limit_mb: tier.storage_mb });
          setTiers(prev => prev); // tiers already updated by loadStorageTiers inside the modal
          setPlansOpen(false);
        }}
      />
    </div>
  );
}

// ── Main UserDashboard ────────────────────────────────────────────────────────
const UserDashboard = () => {
  const [activeTab,    setActiveTab]    = useState('overview');
  const [isCartOpen,   setIsCartOpen]   = useState(false);
  const [showRoleInfo, setShowRoleInfo] = useState(false);
  const [addedId,      setAddedId]      = useState(null);

  const { wishlist, addToCart, cart } = useEcommerce();
  const { user, logout, permissions }  = useAuth();

  const isAdmin      = permissions.isGlobalAdmin();
  const isSuperAdmin = permissions.isSuperAdmin();

  const roleLabel = isSuperAdmin ? 'Super Administrator' : isAdmin ? 'Site Administrator' : 'Memorial Creator';
  const roleBadgeClass = isSuperAdmin
    ? 'bg-gradient-to-r from-purple-700 to-accent-navy text-white border-purple-700'
    : isAdmin
      ? 'bg-accent-navy text-white border-accent-navy'
      : 'bg-accent-gold/20 text-accent-gold border-accent-gold/30';

  const handleAddToCart = (product) => {
    addToCart(product);
    setAddedId(product.id);
    setTimeout(() => { setAddedId(null); setIsCartOpen(true); }, 800);
  };

  const TABS = [
    { id: 'overview',      label: 'Overview'        },
    { id: 'events',        label: 'My Events',       icon: FiRadio      },
    { id: 'family',        label: 'Family Network',  icon: FiUsers      },
    { id: 'legacy-chain',  label: 'Legacy Chain',    icon: FiGitBranch  },
    { id: 'docuvault',     label: 'DocuVault',       icon: FiShield     },
    { id: 'wishlist',      label: 'Wishlist'         },
    { id: 'orders',        label: 'Orders'           },
  ];

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-6">

        <AnimatePresence>
          {showRoleInfo && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} className="mb-8 overflow-hidden">
              <div className="bg-accent-navy text-white p-6 rounded-[32px] border border-white/10 shadow-xl flex gap-6 items-start">
                <div className="p-3 bg-white/10 rounded-2xl shrink-0">
                  <SafeIcon icon={FiInfo} className="w-6 h-6 text-accent-gold" />
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-serif mb-4">Understanding Your Scope of Control</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {ROLE_INFO.map(({ label, desc }) => (
                      <div key={label} className="space-y-2">
                        <p className="text-xs font-black uppercase text-accent-gold tracking-widest">{label}</p>
                        <p className="text-sm text-white/70 font-light">{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <button onClick={() => setShowRoleInfo(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Profile Header */}
        <div className="bg-white rounded-[40px] p-8 md:p-12 border border-brand-dark shadow-sm mb-10 overflow-hidden relative">
          {isSuperAdmin && <div className="absolute inset-0 bg-gradient-to-br from-purple-900/5 via-transparent to-accent-navy/5 pointer-events-none" />}
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent-gold/5 rounded-full -mr-32 -mt-32 blur-3xl" />
          <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
            <div className="relative">
              <div className="w-32 h-32 rounded-[40px] overflow-hidden border-4 border-brand-light shadow-xl">
                <img src={user?.avatar || `https://i.pravatar.cc/150?u=${user?.email}`} alt={user?.name} className="w-full h-full object-cover" />
              </div>
              {isSuperAdmin && (
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-accent-gold rounded-2xl flex items-center justify-center shadow-lg border-2 border-white">
                  <SafeIcon icon={FiCrown} className="w-5 h-5 text-accent-navy" />
                </div>
              )}
            </div>
            <div className="text-center md:text-left flex-grow">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2 flex-wrap">
                <h1 className="text-4xl font-serif text-accent-navy">{user?.name || 'User'}</h1>
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${roleBadgeClass}`}>
                  {roleLabel}
                </span>
                <button onClick={() => setShowRoleInfo(!showRoleInfo)} className="p-1.5 text-text-muted hover:text-accent-gold transition-colors">
                  <SafeIcon icon={FiInfo} className="w-4 h-4" />
                </button>
              </div>
              {isSuperAdmin && (
                <p className="text-[10px] font-black uppercase tracking-widest text-purple-700 mb-3">
                  ⚡ Full Platform Authority — {user?.email}
                </p>
              )}
              <p className="text-text-muted font-light mb-6">
                Preserving memories since {user?.created_at ? new Date(user.created_at).getFullYear() : '2024'}
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                {isAdmin ? (
                  <Link to="/creator-admin"
                    className={`py-2.5 px-6 text-sm flex items-center gap-2 rounded-full font-black uppercase tracking-widest text-white shadow-lg transition-all hover:-translate-y-0.5 ${isSuperAdmin ? 'bg-gradient-to-r from-purple-700 to-accent-navy' : 'bg-accent-navy'}`}>
                    <SafeIcon icon={isSuperAdmin ? FiCrown : FiShield} />
                    {isSuperAdmin ? 'Super Admin Hub' : 'Site Administration'}
                  </Link>
                ) : (
                  <Link to="/explore" className="btn-accent py-2.5 px-6 text-sm flex items-center gap-2">
                    <SafeIcon icon={FiActivity} /> Creator Portal
                  </Link>
                )}
                <Link to="/legacy-chain"
                  className="py-2.5 px-6 text-sm flex items-center gap-2 rounded-full font-black uppercase tracking-widest border-2 border-accent-navy text-accent-navy hover:bg-accent-navy hover:text-white transition-all">
                  <SafeIcon icon={FiGitBranch} className="w-4 h-4" /> Legacy Chain
                </Link>
                <button onClick={() => setIsCartOpen(true)}
                  className="relative btn-secondary py-2.5 px-6 text-sm flex items-center gap-2">
                  <SafeIcon icon={FiShoppingBag} /> Cart
                  {cart.length > 0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-accent-gold text-white text-[9px] font-black rounded-full flex items-center justify-center shadow-lg">
                      {cart.length}
                    </span>
                  )}
                </button>
                <button onClick={logout} className="p-3 text-red-400 hover:text-red-500 transition-colors" title="Sign out">
                  <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 border-b border-brand-dark mb-10 overflow-x-auto hide-scrollbar">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 text-[10px] font-black uppercase tracking-widest transition-all relative flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'text-accent-gold' : 'text-text-muted'}`}>
              {tab.icon && <SafeIcon icon={tab.icon} className="w-3.5 h-3.5" />}
              {tab.label}
              {activeTab === tab.id && (
                <motion.div layoutId="userTab" className="absolute bottom-0 left-0 right-0 h-1 bg-accent-gold rounded-t-full" />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-10">
                <UserStatsOverview />
                <ManagedMemorialsList memorials={mockMemorials} />
              </div>
              <div className="space-y-8">
                <RecentTributesHistory />
                <MilestoneWidget milestones={mockMemorials[0].milestones} name={mockMemorials[0].name} />
              </div>
            </motion.div>
          )}

          {activeTab === 'events' && (
            <motion.div key="events" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <MyEventsTab />
            </motion.div>
          )}

          {activeTab === 'family' && (
            <motion.div key="family" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <FamilyNetworkTab />
            </motion.div>
          )}

          {activeTab === 'legacy-chain' && (
            <motion.div key="legacy-chain" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white rounded-[40px] border border-brand-dark p-8 shadow-sm text-center py-16">
                <div className="w-20 h-20 bg-gradient-to-br from-accent-navy to-purple-700 rounded-[28px] flex items-center justify-center mx-auto mb-6 shadow-xl">
                  <SafeIcon icon={FiGitBranch} className="w-10 h-10 text-accent-gold" />
                </div>
                <h3 className="text-3xl font-serif text-accent-navy mb-3">Legacy Chain</h3>
                <p className="text-text-muted font-light mb-8 max-w-md mx-auto">
                  Explore the full interactive cross-memorial family graph, visualising how families interconnect across generations and memorials on the platform.
                </p>
                <Link to="/legacy-chain"
                  className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-accent-navy to-purple-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-xl shadow-accent-navy/20">
                  <SafeIcon icon={FiGitBranch} className="w-5 h-5" />
                  Open Legacy Chain
                </Link>
              </div>
            </motion.div>
          )}

          {activeTab === 'docuvault' && (
            <motion.div key="docuvault" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <DocuVaultTab />
            </motion.div>
          )}

          {activeTab === 'wishlist' && (
            <motion.div key="wishlist" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white p-12 rounded-[50px] border border-brand-dark shadow-sm">
                <div className="flex items-center gap-4 mb-12">
                  <div className="w-12 h-12 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center border border-red-100">
                    <SafeIcon icon={FiHeart} />
                  </div>
                  <div>
                    <h3 className="text-3xl font-serif text-accent-navy">Your Saved Tokens</h3>
                    <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                      {wishlist.length} item{wishlist.length !== 1 ? 's' : ''} saved
                    </p>
                  </div>
                </div>
                {wishlist.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon={FiHeart} className="w-10 h-10 text-red-300" />
                    </div>
                    <p className="text-text-muted font-light mb-8 italic">"You haven't saved any memorial items yet."</p>
                    <Link to="/marketplace" className="btn-primary py-4 px-10 text-[10px] font-black uppercase tracking-widest">Browse Marketplace</Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {wishlist.map(product => (
                      <motion.div key={product.id} layout
                        className="bg-brand-light p-6 rounded-[32px] border border-brand-dark group hover:border-accent-gold transition-all">
                        <div className="relative overflow-hidden rounded-2xl mb-6 h-40">
                          <img src={product.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={product.name} />
                        </div>
                        <h4 className="font-serif text-xl text-accent-navy mb-1 line-clamp-1">{product.name}</h4>
                        <p className="text-[10px] font-black text-accent-gold uppercase mb-5 mt-2">${product.price?.toLocaleString()}</p>
                        <button onClick={() => handleAddToCart(product)} disabled={addedId === product.id}
                          className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md active:scale-95 ${addedId === product.id ? 'bg-green-500 text-white' : 'bg-[#D2F53E] text-accent-navy hover:bg-[#C0E42C]'}`}>
                          <SafeIcon icon={addedId === product.id ? FiCheck : FiShoppingBag} />
                          {addedId === product.id ? 'Added!' : 'Add to Cart'}
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'orders' && (
            <motion.div key="orders" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-white p-12 rounded-[50px] border border-brand-dark shadow-sm text-center py-24">
                <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiShoppingBag} className="w-10 h-10 text-accent-gold/40" />
                </div>
                <h3 className="text-3xl font-serif text-accent-navy mb-3">No Orders Yet</h3>
                <p className="text-text-muted font-light mb-8 max-w-sm mx-auto">
                  Your memorial product orders will appear here once you've made a purchase.
                </p>
                <Link to="/marketplace" className="btn-primary py-4 px-10 text-[10px] font-black uppercase tracking-widest">Browse Marketplace</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default UserDashboard;